import NoteHolder from './noteHolder';
import Fraction from './fraction';
import utils from '../utils';
import Rhythm from './rhythm';

const ratioSymbol = Symbol('ratio');

/**
 * Representing a tuplet group
 */
export default class Tuplet {
  noteHolders: NoteHolder[];

  /**
   * Create a Tuplet class
   * @param {(string|Fraction)} ratio string num_notes:notes_occupied or fraction.
   * @param {NoteHolder[]} [noteHolders] the noteHolders to group together
   */
  constructor(ratioString: string, noteHolders?: NoteHolder[]);
  constructor(ratioFraction: Fraction, noteHolders?: NoteHolder[]);
  constructor(ratio, noteHolders = []) {
    this.noteHolders = [];
    this.ratio = ratio;
    noteHolders.forEach((nh) => this.addNoteHolder(nh));
  }

  /**
   * @param {string} ratio num_notes:notes_occupied
   */
  setRatio(ratio: string);
  /**
   * @param {Fraction} ratio num_notes/notes_occupied
   */
  setRatio(ratio: Fraction);
  setRatio(ratio) {
    this[ratioSymbol] = processRatio(ratio);
  }

  set ratio(ratio: Fraction) {
    this[ratioSymbol] = processRatio(ratio);
  }

  /** Fraction representing number_of_notes/in_space_of_this_many_notes */
  get ratio(): Fraction {
    return this[ratioSymbol];
  }

  /** upper part of tuplet ratio; how many notes to fit */
  get numNotes(): number {
    return this.ratio.numerator;
  }

  /** lower part of tuplet ratio, i.e. in_the_space_of_this_many_notes */
  get notesOccupied(): number {
    return this.ratio.denominator;
  }

  /**
   * Add a noteHolder to this tuplet
   * @param {NoteHolder} noteHolder
   */
  addNoteHolder(noteHolder: NoteHolder) {
    validNoteHolder(noteHolder);
    // don't add it twice!
    if (this.noteHolders.includes(noteHolder)) return;
    this.noteHolders.push(noteHolder);
    noteHolder.rhythm.addTuplet(this);
  }

  /**
   * Remove a NoteHolder from this tuplet (if it is part of this tuplet)
   * @param {NoteHolder} noteHolder
   */
  removeNoteHolder(noteHolder: NoteHolder) {
    validNoteHolder(noteHolder);
    const index = this.noteHolders.indexOf(noteHolder);
    if (index === -1) return;
    noteHolder.rhythm.removeTuplet(this);
    this.noteHolders.splice(index, 1);
  }

  toString(): string {
    if (this.ratio.equals(new Fraction(3, 2))) {
      return 'triplet';
    }
    if (this.ratio.equals(new Fraction(5, 4))) {
      return 'quintuplet';
    }
    if (this.ratio.equals(new Fraction(7, 8))) {
      return 'septuplet';
    }
    return `${this.numNotes}:${this.notesOccupied} tuplet`;
  }
}

function validNoteHolder(noteHolder) {
  if (!(noteHolder instanceof NoteHolder)) {
    throw new Error(`addNoteHolder expected type NoteHolder, got ${utils.typeof(noteHolder)}`);
  }
  if (!(noteHolder.rhythm instanceof Rhythm)) {
    throw new Error('noteHolder must have a rhythm prop to join or leave a tuplet.');
  }
}

/**
 * @param {string} ratio
 */
function processRatio(ratio: string): Fraction;
/**
 * @param {Fraction} ratio
 */
function processRatio(ratio: Fraction): Fraction;
function processRatio(ratio) {
  if (ratio instanceof Fraction) return ratio.simplify();
  if (!utils.isString(ratio)) {
    throw new Error(`Tuplet ratio must be string or Fraction, not ${utils.typeof(ratio)}`);
  }
  const [numNotes, notesOccupied] = ratio.split(/\/|:/).map(Number);
  return new Fraction(numNotes, notesOccupied);
}
